import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { ScrollView } from 'react-native';
import { getSelectedData, getSentences } from '@/api/services/sentenceset';
import { getTags, TagResponse } from '@/api/services/wordset';
import {
  ARRAY_STATE,
  CHANGE_PAGE,
  OUTER_STATE,
  SENTENCE_STATE,
  SUBJECT_STATE,
} from './constants';
import { logError } from '@/utils/logHandling';
import { SentenceType, SubjectType } from '@/screens/SentenceSet/config';
import { getTemplate } from '@/api/services/templates';
import { Template } from '@/components/Template/types';
import {
  ActionType,
  Sentence,
  SentenceSetContextType,
  StateType,
} from './types';

export type SentenceState = StateType & {
  data?: { totalCount: number; sentences: Sentence[] };
  selectedData?: Sentence[];
  limitModal?: boolean;
  editData?: SentenceSetContextType | null;
  handleOuterState: (name: string, value: unknown) => void;
  handleArrayState: (value: string, isRemove: boolean) => void;
  handleSentenceTypeState: (
    value: SentenceType,
    sentenceTypeArray: string[],
  ) => void;
  handleSubjectTypeState: (
    value: SubjectType,
    sentenceTypeArray: string[],
  ) => void;
  changeData?: (newData: { totalCount: number; sentences: Sentence[] }) => void;
  changeSelectedData?: Dispatch<SetStateAction<Sentence[]>>;
  changePage?: (value: number, arrowName?: 'right' | 'left' | null) => void;
  changeLimitModal?: () => void;
  refetchSentences?: () => void;
  patientId?: string;
  setId?: string;
  apiName?: string;
  scrollViewRef?: React.RefObject<ScrollView | null>;
  tags?: string[];
  template?: Template | null;
};

const INITIAL_STATE: StateType = {
  sentenceSubTypes: ['standard', 'particleVerb'],
  term: '',
  termType: 'sentence',
  sentenceType: SentenceType.OV,
  subjectType: SubjectType.NOMEN,
  searchSentencesPerPage: 30,
  page: 1,
};

export const SentenceSetContext = createContext<SentenceState>(
  INITIAL_STATE as SentenceState,
);

const reducer = (state: StateType, action: ActionType): StateType => {
  const { type, payload } = action;

  switch (type) {
    case OUTER_STATE: {
      const { name, value } = payload;
      return {
        ...state,
        page: 1,
        [name]: value,
      };
    }

    case SENTENCE_STATE: {
      const { value, sentenceTypeArray } = payload;
      return {
        ...state,
        page: 1,
        sentenceType: value,
        sentenceSubTypes: sentenceTypeArray,
      };
    }

    case SUBJECT_STATE: {
      const { value, sentenceTypeArray } = payload;
      return {
        ...state,
        page: 1,
        sentenceSubTypes: sentenceTypeArray,
        subjectType: value,
      };
    }

    case CHANGE_PAGE: {
      const { value, arrowName } = payload;
      if (arrowName === null) {
        return {
          ...state,
          page: value,
        };
      } else {
        if (arrowName === 'right') {
          return {
            ...state,
            page: state.page + 1,
          };
        } else {
          return {
            ...state,
            page: state.page - 1,
          };
        }
      }
    }

    case ARRAY_STATE: {
      const { value, isRemove } = payload;
      if (!isRemove) {
        return {
          ...state,
          page: 1,
          sentenceSubTypes: [...state.sentenceSubTypes, value],
        };
      } else {
        return {
          ...state,
          page: 1,
          sentenceSubTypes: state.sentenceSubTypes.filter(
            (eachType) => eachType !== value,
          ),
        };
      }
    }

    default:
      return state;
  }
};

interface SentenceSetContextProviderProps {
  children: React.ReactNode;
  patientId?: string;
  setId?: string;
  fromTemplate?: string;
  apiName: string;
}

export function SentenceSetContextProvider({
  children,
  patientId,
  setId,
  apiName,
  fromTemplate,
}: SentenceSetContextProviderProps) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [tags, setTags] = useState<Pick<TagResponse, 'sentences'> | null>(null);
  const scrollViewRef = useRef<ScrollView | null>(null);

  const [data, setData] = useState<{
    sentences: Sentence[];
    totalCount: number;
  }>({
    sentences: [],
    totalCount: 0,
  });
  const [limitModal, setLimitModal] = useState(false);
  const [selectedData, setSelectedData] = useState<Sentence[]>([]);
  const [template, setTemplate] = useState<Template | null>(null);
  const componentMounted = useRef(false);
  const requestRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editData = useRef<SentenceSetContextType | null>(null);

  const changePage = useCallback(
    (value: number, arrowName: 'right' | 'left' | null = null) => {
      dispatch({
        type: CHANGE_PAGE,
        payload: { value, arrowName },
      });
      if (scrollViewRef.current && value) {
        scrollViewRef.current.scrollTo({ y: 150, animated: true });
      }
    },
    [],
  );

  const handleOuterState = useCallback((name: string, value: unknown) => {
    dispatch({
      type: OUTER_STATE,
      payload: { name: name as keyof StateType, value },
    });
  }, []);

  const handleSentenceTypeState = useCallback(
    (value: SentenceType, sentenceTypeArray: string[]) => {
      dispatch({
        type: SENTENCE_STATE,
        payload: { value, sentenceTypeArray },
      });
    },
    [],
  );

  const handleSubjectTypeState = useCallback(
    (value: SubjectType, sentenceTypeArray: string[]) => {
      dispatch({
        type: SUBJECT_STATE,
        payload: { value, sentenceTypeArray },
      });
    },
    [],
  );

  const handleArrayState = useCallback((value: string, isRemove: boolean) => {
    dispatch({
      type: ARRAY_STATE,
      payload: { value, isRemove },
    });
  }, []);

  const changeLimitModal = useCallback(() => {
    setLimitModal((prevState) => !prevState);
  }, []);
  const changeSelectedData = useCallback(
    (newSelectedData: SetStateAction<Sentence[]>) => {
      setSelectedData(newSelectedData);
    },
    [],
  );

  const changeData = useCallback(
    (newData: { totalCount: number; sentences: Sentence[] }) => {
      setData(newData);
    },
    [],
  );

  const handleFetchSentences = useCallback(async (object: StateType) => {
    try {
      let payload;
      if (object?.termType === 'sentence' || object?.termType === 'tag') {
        payload = { ...object, term: object?.term.trim() };
      } else {
        payload = { ...object };
      }

      const { data } = await getSentences({
        ...payload,
        sentenceSubTypes: payload.sentenceSubTypes,
        subjectType:
          object.sentenceType === SentenceType.OV ||
          object.sentenceType === SentenceType.VO
            ? undefined
            : payload.subjectType,
      });

      setData(data);
    } catch (error) {
      logError('Could not fetch sentences', error);
    }
  }, []);

  const handleFetchTags = useCallback(async () => {
    try {
      const { data } = await getTags();
      if (data) {
        const { sentences } = data;
        setTags({ sentences });
      }
    } catch (error) {
      logError('Could not fetch tags', error);
    }
  }, []);

  const handleFetchSelectedSentences = useCallback(
    async (apiName: string, patientId: string | undefined, setId: string) => {
      try {
        const { data } = await getSelectedData(apiName, patientId, setId);
        editData.current = data;
        const { sentences } = data;
        setSelectedData(sentences);
      } catch (error) {
        logError('Could not fetch selected sentences', error);
      }
    },
    [],
  );

  const refetchSentences = useCallback(() => {
    handleFetchSentences(state);
  }, [state]);

  useEffect(() => {
    if (componentMounted.current && state.page > 0) {
      clearTimeout(requestRef.current ?? undefined);
      requestRef.current = setTimeout(() => {
        handleFetchSentences(state);
      }, 500);
    }
  }, [state.term]);

  useEffect(() => {
    if (componentMounted.current && state.page > 0) {
      handleFetchSentences(state);
    }
  }, [
    state.sentenceType,
    state.subjectType,
    state.sentenceSubTypes,
    state.page,
    state.termType,
  ]);

  useEffect(() => {
    const fetchedSentences = async () => {
      if (!setId) {
        if (fromTemplate) {
          const template = await getTemplate(fromTemplate);
          setTemplate(template);

          const sentencesetItemsMapped = (template.items ?? [])
            .filter((item) => item.active)
            .map((item) => item.sentence)
            .filter((sentence): sentence is Sentence => sentence !== undefined);
          setSelectedData(sentencesetItemsMapped);
          handleFetchSentences(state);
        } else {
          handleFetchSentences(state);
        }
        handleFetchTags();
      } else {
        try {
          handleFetchTags();
          handleFetchSentences(state);
          handleFetchSelectedSentences(apiName, patientId, setId);
        } catch (error) {
          logError('Could not fetch', error);
        }
      }
      componentMounted.current = true;
    };
    fetchedSentences();
  }, [fromTemplate]);

  const value: SentenceState = {
    data,
    selectedData,
    limitModal,
    editData: editData.current,
    page: state.page,
    searchSentencesPerPage: state.searchSentencesPerPage,
    term: state.term,
    termType: state.termType,
    sentenceType: state.sentenceType,
    subjectType: state.subjectType,
    sentenceSubTypes: state.sentenceSubTypes,
    handleOuterState,
    handleArrayState,
    handleSentenceTypeState,
    handleSubjectTypeState,
    changeData,
    changeSelectedData,
    changePage,
    changeLimitModal,
    refetchSentences,
    patientId,
    setId,
    apiName,
    scrollViewRef,
    tags: tags?.sentences?.map((item) => item.displayHierarchy) ?? [],
    template,
  };

  return (
    <SentenceSetContext.Provider value={value}>
      {children}
    </SentenceSetContext.Provider>
  );
}
